import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Maximize2 } from 'lucide-react';
import { EXPLORE } from '@/content';
import { track } from '@/lib/track';
import FullscreenViewer from '@/components/FullscreenViewer';
import RedO from '@/components/RedO';

export default function Explore() {
  const [active, setActive] = useState(null);
  const [zoom, setZoom] = useState(false);
  const IMG = EXPLORE.image;

  const open = (e, h) => {
    e.stopPropagation();
    setActive(h);
    track('map_interaction', { type: 'building_hotspot', id: h.id });
  };

  return (
    <section id="explore" data-testid="explore-section" className="relative scroll-mt-20 bg-white py-20 sm:py-28" aria-labelledby="explore-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow">{EXPLORE.eyebrow}</p>
            <h2 id="explore-heading" className="headline mt-3 text-3xl sm:text-5xl">
              <RedO text={EXPLORE.headline} />
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#404145]">{EXPLORE.sub}</p>
        </div>

        <motion.div
          className="relative mt-10 overflow-hidden rounded-3xl border border-[#E4E6EB] shadow-[0_40px_90px_-50px_rgba(28,29,33,0.45)]"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* natural-aspect container matches the stitched spread exactly — image never cropped,
              hotspot % coordinates are relative to the image itself at every breakpoint */}
          <div
            className="relative w-full cursor-zoom-in bg-[#F4F3EE]"
            style={{ aspectRatio: `${IMG.width} / ${IMG.height}` }}
            onClick={() => setZoom(true)}
            data-testid="explore-image-area"
          >
            <picture>
              <img
                src={IMG.full}
                srcSet={IMG.srcset}
                sizes="(min-width: 1280px) 1232px, 100vw"
                width={IMG.width}
                height={IMG.height}
                alt={IMG.alt}
                className="absolute inset-0 h-full w-full object-contain"
                loading="lazy"
                decoding="async"
              />
            </picture>
            {EXPLORE.hotspots.map((h) => (
              <button
                key={h.id}
                data-testid={`hotspot-${h.id}`}
                aria-label={`${h.label} — details`}
                aria-expanded={active?.id === h.id}
                onClick={(e) => open(e, h)}
                className="hotspot-ring absolute z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-[#C92F4B]/90 text-white shadow-lg backdrop-blur-sm transition-transform hover:scale-110 active:scale-95"
                style={{ left: `${h.x}%`, top: `${h.y}%` }}
              >
                <Plus size={18} />
              </button>
            ))}
            <button
              data-testid="explore-expand-btn"
              aria-label="View elevation full screen"
              onClick={(e) => { e.stopPropagation(); setZoom(true); }}
              className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1D21]/85 text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
            >
              <Maximize2 size={17} />
            </button>
          </div>
          {zoom && <FullscreenViewer src={IMG.full} alt={IMG.alt} onClose={() => setZoom(false)} />}

          {/* desktop side panel */}
          <AnimatePresence>
            {active && (
              <motion.aside
                key={active.id}
                data-testid="hotspot-panel"
                className="absolute bottom-4 left-4 right-4 z-20 hidden rounded-2xl border border-[#E4E6EB] bg-white p-6 shadow-[0_24px_60px_-20px_rgba(28,29,33,0.45)] md:bottom-auto md:left-auto md:right-6 md:top-6 md:block md:w-80"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-[10px] font-bold uppercase tracking-[0.24em] text-[#A41225]">Aarohi Complex</p>
                    <h3 className="mt-1 font-display text-xl font-extrabold uppercase text-[#1C1D21]">{active.label}</h3>
                  </div>
                  <button
                    data-testid="hotspot-panel-close"
                    aria-label="Close details"
                    onClick={() => setActive(null)}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1C1D21] text-white transition-transform hover:scale-105 active:scale-95"
                  >
                    <X size={16} />
                  </button>
                </div>
                <p className="mt-3 text-base leading-relaxed text-[#404145]">{active.detail}</p>
              </motion.aside>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* mobile bottom sheet */}
      <AnimatePresence>
        {active && (
          <>
            <motion.div
              className="fixed inset-0 z-[70] bg-[#1C1D21]/40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActive(null)}
            />
            <motion.div
              data-testid="hotspot-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={active.label}
              className="safe-bottom fixed inset-x-0 bottom-0 z-[71] max-h-[75vh] overflow-y-auto rounded-t-3xl bg-white p-6 pb-8 shadow-2xl md:hidden"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              drag="y"
              dragConstraints={{ top: 0 }}
              dragElastic={0.4}
              onDragEnd={(_, info) => info.offset.y > 90 && setActive(null)}
            >
              <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-[#E4E6EB]" />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-[10px] font-bold uppercase tracking-[0.24em] text-[#A41225]">Aarohi Complex</p>
                  <h3 className="mt-1 font-display text-xl font-extrabold uppercase text-[#1C1D21]">{active.label}</h3>
                </div>
                <button
                  data-testid="hotspot-sheet-close"
                  aria-label="Close details"
                  onClick={() => setActive(null)}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1C1D21] text-white transition-transform hover:scale-105 active:scale-95"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="mt-3 text-base leading-relaxed text-[#404145]">{active.detail}</p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
